











































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import DataViewerComponent from "@/components/common/data-viewer/DataViewer.vue";
import { ViewerSection, CellEditEvent } from "@/components/common/data-viewer/DataViewerModels";
import { parseBeschreibungCsv } from "@/utils/BeschreibungCsvParser";
import { editDataViewerAccess } from "@/permissions/UserPermissions";
import { getActiveUser } from "@/data/repository/UserRepository";
import Report from "@/data/models/Report";
import { getDownloadUrl } from "@/data/repository/FirebaseImageRepository";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

/**
 * Beschreibung (Gewerke) CSV Viewer component for the Bericht tab.
 * Fetches a semicolon-delimited CSV from Firebase Storage and displays
 * it as collapsible sections using the shared DataViewer.
 */

@Component({
  components: {
    "data-viewer": DataViewerComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class BeschreibungViewer extends Vue {
  /** The Beschreibung report to display */
  @Prop() report!: Report;

  @Ref("dataViewer") dataViewerComponent!: DataViewerComponent;
  @Ref("confirm") confirmationDialogComponent!: ConfirmationDialogComponent;

  /** Whether the viewer has unsaved edits */
  /**
   * Icon of the file this button produces.
   *
   * A Beschreibung is a CSV, and this downloads the stored file unchanged.
   */
  get downloadIcon(): string {
    return getFileTypeIcon(DownloadFileType.CSV);
  }

  get hasUnsavedChanges(): boolean {
    return this.dataViewerComponent?.hasChanges ?? false;
  }

  isLoading = false;
  isSaving = false;
  errorMessage = "";
  downloadUrl = "";
  rawCsvText = "";
  viewerSections: ViewerSection[] = [];

  get viewerTitle(): string {
    return this.$t("beschreibungViewer.title") as string;
  }

  get canEdit(): boolean {
    return editDataViewerAccess(getActiveUser());
  }

  /** Derive download filename from report fields */
  get downloadFileName(): string {
    const name = this.report?.fileNameToDownload
      ?? this.report?.fileNameToDisplay
      ?? this.report?.fileName
      ?? this.report?.storageLocation?.split("/").pop()
      ?? "beschreibung";
    return name.endsWith(".csv") ? name : `${name}.csv`;
  }

  async mounted() {
    await this.loadCsvData();
  }

  @Watch("report")
  async onReportChanged() {
    await this.loadCsvData();
  }

  private async loadCsvData() {
    const storageLocation = this.report?.storageLocation;
    if (!storageLocation) {
      this.errorMessage = this.$t("beschreibungViewer.noStorageLocation") as string;
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";

    try {
      const url = await getDownloadUrl(storageLocation);
      this.downloadUrl = url;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
      }
      this.rawCsvText = await response.text();
      this.viewerSections = parseBeschreibungCsv(this.rawCsvText);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BeschreibungViewer] Failed to load CSV data:", error);
      this.errorMessage = this.$t("beschreibungViewer.loadError") as string;
    } finally {
      this.isLoading = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCellEdit(_event: CellEditEvent) {
    // Edits are tracked in DataViewer's pendingEdits — no mutation here.
  }

  /** Check for unsaved changes, then close if confirmed. */
  async onClose() {
    if (await this.confirmDiscardIfNeeded()) {
      this.$emit("close");
    }
  }

  /**
   * Checks for unsaved changes and shows a discard dialog if needed.
   * Returns true if safe to proceed (no changes or user confirmed discard).
   */
  async confirmDiscardIfNeeded(): Promise<boolean> {
    if (!this.hasUnsavedChanges) return true;
    return await this.confirmationDialogComponent.show({
      title: this.$t("common.confirmationDialog.cancelTitle").toString(),
      message: this.$t("common.confirmationDialog.cancelMessage").toString(),
    });
  }

  /** Revert displayed data to the original fetched state */
  onDiscard() {
    if (this.rawCsvText) {
      this.viewerSections = parseBeschreibungCsv(this.rawCsvText);
    }
  }

  /** Force-download the CSV file instead of opening in browser */
  async onDownload() {
    if (!this.downloadUrl) return;
    try {
      const response = await fetch(this.downloadUrl);
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = this.downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BeschreibungViewer] Download failed:", error);
    }
  }

  async onSave(edits: CellEditEvent[]) {
    if (!this.rawCsvText || edits.length === 0) return;

    this.isSaving = true;
    try {
      // TODO: Implement save via Cloud Function

      // Clear pending edits in the DataViewer
      this.dataViewerComponent?.clearPendingEdits?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BeschreibungViewer] Failed to save data:", error);
    } finally {
      this.isSaving = false;
    }
  }
}
